/**
 * Import de machines depuis un fichier CSV.
 *
 * Même discipline que l'import des tiers : on simule, on lit, on valide. Le
 * rapport annonce explicitement les sites qui seront créés — c'est la seule
 * donnée que la validation fabrique, et elle doit être vue avant.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type ImportReport, type RowOutcome } from '../api/client';
import { ZoneDepot } from '../composants/ZoneDepot';
import { accorde } from '../domaine/format';

const OUTCOME: Record<RowOutcome, { texte: string; classe: string }> = {
  READY: { texte: 'Prêt', classe: 'actif' },
  WARNING: { texte: 'À vérifier', classe: 'attente' },
  DUPLICATE: { texte: 'Doublon', classe: 'attente' },
  INCOMPLETE: { texte: 'Incomplet', classe: 'suspendu' },
  ERROR: { texte: 'Erreur', classe: 'revoque' },
};

export function AssetImportPage() {
  const [fichier, setFichier] = useState<{ nom: string; contenu: string } | null>(null);
  const [rapport, setRapport] = useState<ImportReport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const oublierFichier = () => setFichier(null);

  const choisir = (f: File | null) => {
    setErreur(null);
    setSucces(null);
    setRapport(null);
    if (!f) {
      setFichier(null);
      return;
    }
    const lecteur = new FileReader();
    lecteur.onload = () => setFichier({ nom: f.name, contenu: String(lecteur.result ?? '') });
    lecteur.onerror = () => setErreur('Lecture du fichier impossible.');
    lecteur.readAsText(f, 'utf-8');
  };

  const simuler = async () => {
    if (!fichier) return;
    setEnCours(true);
    setErreur(null);
    try {
      setRapport(await api.simulateAssetImport(fichier.nom, fichier.contenu));
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Simulation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const valider = async () => {
    if (!rapport) return;
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await api.commitAssetImport(rapport.jobId);
      setSucces(
        `${resultat.created} ${accorde(resultat.created, 'machine')} ` +
          `${accorde(resultat.created, 'créée')}.`,
      );
      setRapport(null);
      oublierFichier();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Validation impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const exploitables = rapport ? rapport.totals.ready + rapport.totals.warning : 0;
  const sitesACreer = rapport
    ? new Set(
        rapport.rows
          .filter((r) => r.outcome === 'READY' || r.outcome === 'WARNING')
          .map((r) => r.preview?.['siteACreer'])
          .filter((s): s is string => typeof s === 'string'),
      ).size
    : 0;

  return (
    <>
      <div className="fil-ariane">
        <Link to="/parc">← Parc</Link>
      </div>

      <div className="entete-page">
        <div>
          <h1>Importer des machines</h1>
          <p>Déposez un fichier CSV. Rien n’est écrit tant que vous n’avez pas validé.</p>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {succes && (
        <div className="message succes">
          {succes} <Link to="/parc">Voir le parc</Link>
        </div>
      )}

      <div className="carte">
        <h2>1. Choisir le fichier</h2>
        <div className="carte-corps">
          <ZoneDepot
            accept=".csv,text/csv"
            libelle="Glissez le fichier CSV ici, ou"
            nomChoisi={fichier?.nom ?? null}
            onFichiers={(fichiers) => choisir(fichiers[0] ?? null)}
          />
          <p className="aide">
            Séparateur point-virgule ou virgule, encodage UTF-8. Colonnes reconnues :{' '}
            <em>Famille</em>, <em>Désignation</em>, <em>Modèle</em>, <em>Numéro de série</em>,{' '}
            <em>Marque</em>, <em>Fabricant</em>, <em>Client</em>, <em>Site</em>,{' '}
            <em>Emplacement</em>, <em>Mise en service</em>, <em>État</em>, <em>Notes</em>.
          </p>
          <p className="aide">
            <strong>Les clients doivent déjà exister</strong> dans vos tiers — une machine chez un
            client inconnu est signalée en erreur. Les <strong>sites manquants</strong>, eux, sont
            annoncés dans le rapport et créés à la validation.
          </p>

          {fichier && (
            <div style={{ marginTop: 14 }}>
              <button onClick={() => void simuler()} disabled={enCours}>
                {enCours ? 'Analyse…' : `Analyser « ${fichier.nom} »`}
              </button>
            </div>
          )}
        </div>
      </div>

      {rapport && (
        <>
          <div className="carte">
            <h2>2. Rapport de simulation</h2>
            <div className="carte-corps">
              <p>
                <strong>
                  {rapport.totals.total} {accorde(rapport.totals.total, 'ligne')}{' '}
                  {accorde(rapport.totals.total, 'analysée')}
                </strong>{' '}
                — {rapport.totals.ready} {accorde(rapport.totals.ready, 'prête')},{' '}
                {rapport.totals.warning} à vérifier, {rapport.totals.duplicate}{' '}
                {accorde(rapport.totals.duplicate, 'doublon')}, {rapport.totals.error} en erreur.
              </p>
              <div className="message info">
                Aucune donnée n’a encore été écrite. La validation créera{' '}
                <strong>
                  {exploitables} {accorde(exploitables, 'machine')}
                </strong>
                {sitesACreer > 0 && (
                  <>
                    {' '}
                    et <strong>{sitesACreer}</strong> nouveau{sitesACreer > 1 ? 'x' : ''} site
                    {sitesACreer > 1 ? 's' : ''}
                  </>
                )}
                . Les doublons et les lignes en erreur seront ignorés, pas bloquants.
              </div>
              <button onClick={() => void valider()} disabled={enCours || exploitables === 0}>
                {enCours
                  ? 'Import en cours…'
                  : `Valider et créer ${exploitables} ${accorde(exploitables, 'machine')}`}
              </button>
            </div>
          </div>

          <div className="carte">
            <h2>Détail ligne par ligne</h2>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Ligne</th>
                  <th style={{ width: 110 }}>État</th>
                  <th>Machine</th>
                  <th>Implantation</th>
                  <th>Observations</th>
                </tr>
              </thead>
              <tbody>
                {rapport.rows.map((r) => {
                  const etat = OUTCOME[r.outcome];
                  const p = r.preview ?? {};
                  const designation =
                    (p['label'] as string | null) ?? (p['model'] as string | null) ?? '—';
                  return (
                    <tr key={r.lineNumber}>
                      <td>{r.lineNumber}</td>
                      <td>
                        <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                      </td>
                      <td>
                        <strong>{designation}</strong>
                        <div className="aide">
                          {(p['assetType'] as string | null) ?? '—'}
                          {p['serialNumber'] ? ` · ${p['serialNumber'] as string}` : ''}
                        </div>
                      </td>
                      <td>
                        {(p['customerName'] as string | null) ?? (
                          <span className="aide">client absent</span>
                        )}
                        {p['siteName'] ? <div className="aide">{p['siteName'] as string}</div> : null}
                      </td>
                      <td>
                        {r.messages.length === 0 ? (
                          <span className="aide">—</span>
                        ) : (
                          r.messages.map((m, i) => (
                            <div key={i} className="aide">
                              {m}
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
